import { VideoStatus } from 'src/Constants/enums'
import {
  CampaignStatusEnum,
  TikTokCampaignPricingModelEnum,
} from 'src/Types/campaigns'
import { CampaignPreModerationVideo } from 'src/Redux/campaignVideosApi/mapVideoToPreModerationStatus'
import { endedStatuses } from 'src/Components/TrackInfo'

export interface CampaignGoalValues {
  currentValue: number
  minValue: number
  maxValue: number
  calculatedPercentage: number
}

type GoalValue = number | string | undefined

const parseGoalValue = (value: GoalValue): number => {
  if (typeof value === 'number') {
    return value
  }

  if (!value) {
    return 0
  }

  const normalizedValue = value.toLowerCase().trim()

  if (normalizedValue.endsWith('k')) {
    return Number(normalizedValue.replace('k', '')) * 1000
  }

  return Number(normalizedValue) || 0
}

export const getCampaignGoalValues = (
  pricingModel: TikTokCampaignPricingModelEnum | undefined,
  viewsCount = 0,
  videosCount = 0,
  expectedMinViews?: string,
  expectedMinVideos?: number,
  expectedMaxViews?: string,
  expectedMaxVideos?: number,
): CampaignGoalValues => {
  const isViewsBased =
    pricingModel === TikTokCampaignPricingModelEnum.ViewsBased

  const currentValue = isViewsBased ? viewsCount : videosCount

  const minValue = parseGoalValue(
    isViewsBased ? expectedMinViews : expectedMinVideos,
  )

  const maxValue = parseGoalValue(
    isViewsBased ? expectedMaxViews : expectedMaxVideos,
  )

  const calculatedPercentage = minValue
    ? Math.round((currentValue / minValue) * 100)
    : 0

  return {
    currentValue,
    minValue,
    maxValue,
    calculatedPercentage,
  }
}

const passedPreModerationVideoStatuses = [
  VideoStatus.AWAITING_REVIEW,
  VideoStatus.APPROVED_BY_ARTIST,
  VideoStatus.APPROVED_AUTOMATICALLY,
  VideoStatus.APPROVED_BY_ADMIN,
  VideoStatus.AWAITING_MODERATOR_REVIEW,
]

export const hasVideoPassedPreModeration = (
  videos: Array<CampaignPreModerationVideo> | undefined,
  campaignStatus: CampaignStatusEnum,
): boolean =>
  endedStatuses.includes(campaignStatus) ||
  Boolean(
    videos?.some((video) =>
      passedPreModerationVideoStatuses.includes(video.status),
    ),
  )

export const getHasDismissedOrDeclinedVideos = (
  videos: Array<CampaignPreModerationVideo> | undefined,
): boolean =>
  Boolean(
    videos?.some(
      (video) =>
        video.status === VideoStatus.DISMISSED ||
        video.status === VideoStatus.DECLINED,
    ),
  )

export const getShowExpectationCards = (
  pricingModel: TikTokCampaignPricingModelEnum | undefined,
  campaignStatus: CampaignStatusEnum,
  videosCount: number | undefined,
  videos: Array<CampaignPreModerationVideo> | undefined,
): boolean => {
  const isViewsBased =
    pricingModel === TikTokCampaignPricingModelEnum.ViewsBased

  const isActiveCampaign =
    campaignStatus === CampaignStatusEnum.RUNNING ||
    campaignStatus === CampaignStatusEnum.INPROCESS

  const hasNoEligibleVideos =
    videosCount === 0 &&
    !videos?.some((video) => video.status === VideoStatus.AWAITING_REVIEW) &&
    !getHasDismissedOrDeclinedVideos(videos)

  return isViewsBased && hasNoEligibleVideos && isActiveCampaign
}
